import {
    PageExtensionAPI,
    StructureExtensionAPI,
    constants,
    type AppsInstallStateExtensionAPI
} from '@wix/document-manager-extensions'
import type {ExtensionAPI, DAL} from '@wix/document-manager-core'
import type {
    AppInstallState,
    AppsInstallStateMap,
    Pointer,
    Pointers,
    PossibleViewModes,
    ViewModes
} from '@wix/document-services-types'
import {styles, limits, pagesConsts, titles, EMPTY_STRING} from './consts'

const {VIEW_MODES} = constants

const {WARNING_STYLE, INFO_STYLE} = styles
const {
    NUMBER_OF_COMPONENTS_PER_PAGE_LIMIT,
    NUMBER_OF_COMPONENTS_LIMIT,
    NUMBER_OF_PAGES_LIMIT,
    NUMBER_OF_CHILD_WITH_SAME_TYPE
} = limits
const {UNKNOWN_PAGE, DATA_NAMESPACE} = pagesConsts
const {
    NUMBER_OF_COMPONENTS_TITLE,
    NUMBER_OF_PAGES_TITLE,
    SUSPICIOUS_COMPONENTS_TITLE,
    SUSPICIOUS_PAGES_TITLE,
    APPS_STATE_TITLE
} = titles

export interface HandlerResult {
    title: string
    content: any
    style: string
}

type HandlerFunction = () => HandlerResult
type HandlerViewFunction = (view: PossibleViewModes) => HandlerResult

export interface DSAnomalyHandler {
    readonly generalSiteHandlers: Record<string, HandlerFunction>
    readonly viewModeSiteHandlers: Record<string, HandlerViewFunction>
}

export const handlers = (extensionAPI: ExtensionAPI, dal: DAL, pointers: Pointers): DSAnomalyHandler => {
    const {components} = extensionAPI as StructureExtensionAPI
    const {page} = extensionAPI as PageExtensionAPI

    const getAllComponents = (viewMode: keyof ViewModes): Record<string, any> => {
        switch (viewMode) {
            case VIEW_MODES.MOBILE:
                return components.getAllMobileComponents()
            case VIEW_MODES.DESKTOP:
                return components.getAllDesktopComponents()
        }
    }

    const getPageData = (pointer: Pointer) => {
        const {id} = pointer
        const pageData = dal.get({id, type: DATA_NAMESPACE})
        return {pageId: id, pageTitle: pageData.title}
    }

    const getComponentPageData = (pointer: Pointer) => {
        const componentData = dal.get(pointer)
        const {componentType} = componentData
        const pageId = componentData?.metaData?.pageId
        if (!pageId) return {pageId: UNKNOWN_PAGE, pageTitle: UNKNOWN_PAGE, componentType}
        return {componentType, ...getPageData({id: pageId, type: pointer.type})}
    }

    const printNumberOfPages = (): HandlerResult => {
        const numberOfPages = page.getAllPagesIds(true).length
        const style = numberOfPages > NUMBER_OF_PAGES_LIMIT ? WARNING_STYLE : INFO_STYLE
        return {
            title: NUMBER_OF_PAGES_TITLE,
            content: numberOfPages,
            style
        }
    }

    const printDiffBetweenAppsStateAndClientSpecMap = (): HandlerResult => {
        const {appsInstallState} = extensionAPI as AppsInstallStateExtensionAPI
        const stateMap: AppsInstallStateMap = appsInstallState.getAllAppsInstallStatus()
        const clientSpecMap = dal.get(pointers.getPointer('clientSpecMap', 'rendererModel'))
        const content = stateMap && clientSpecMap ? generateAppStateDiff() : 'statesMap or clientSpecMap does not exist'
        return {
            title: APPS_STATE_TITLE,
            content,
            style: WARNING_STYLE
        }

        function getFilteredStateMap(): Record<string, AppInstallState> {
            const excludedApps = ['dataBinding', 'wix-code']
            return Object.fromEntries(Object.entries(stateMap).filter(([key]) => !excludedApps.includes(key)))
        }

        function createClientSpecMap(): Record<string, boolean | undefined> {
            return Object.values(clientSpecMap).reduce((specMap: Record<string, boolean | undefined>, appData: any) => {
                if (appData.appDefinitionId) {
                    specMap[appData.appDefinitionId] = appData.permissions?.revoked
                }
                return specMap
            }, {})
        }

        function generateAppStateDiff(): string {
            const specToStateStatusDic = {
                INSTALLED: false,
                REMOVED: true
            }
            const clientSpecMapDic = createClientSpecMap()
            const stateMapWithoutExcludedApps = getFilteredStateMap()
            return Object.entries(stateMapWithoutExcludedApps).reduce((stateDiff, [appKey, appValue]) => {
                if (!appValue.status) {
                    stateDiff += `Application ${appKey} status in appInstallStateMap is undefined but in clientSpecMap is ${clientSpecMapDic[appKey]}\n`
                    return stateDiff
                }

                const appStatus = specToStateStatusDic[appValue.status]
                if (clientSpecMapDic[appKey] !== appStatus) {
                    stateDiff += `Application ${appKey} status in appInstallStateMap is ${appValue.status} but in clientSpecMap is ${clientSpecMapDic[appKey]}\n`
                }
                return stateDiff
            }, EMPTY_STRING)
        }
    }

    const printSuspiciousPages = (vm: PossibleViewModes): HandlerResult => {
        const pages = page.getAllPagesIds(true)
        const suspiciousPagesContent = pages.reduce((content, currentPage) => {
            const compsInPage = Object.keys(page.getAllCompsOnPage(currentPage, vm)).length
            if (compsInPage > NUMBER_OF_COMPONENTS_PER_PAGE_LIMIT) {
                const {pageTitle} = getPageData({id: currentPage, type: vm})
                content += `page id ${currentPage} with page title ${pageTitle} has ${compsInPage} components \n`
            }
            return content
        }, EMPTY_STRING)
        return {
            title: `${SUSPICIOUS_PAGES_TITLE} ${vm}`,
            content: suspiciousPagesContent,
            style: INFO_STYLE
        }
    }
    const printNumberOfComponents = (vm: PossibleViewModes): HandlerResult => {
        const numOfViewComponents = Object.keys(getAllComponents(vm)).length
        const style = numOfViewComponents > NUMBER_OF_COMPONENTS_LIMIT ? WARNING_STYLE : INFO_STYLE
        return {
            title: `${NUMBER_OF_COMPONENTS_TITLE} ${vm}`,
            content: numOfViewComponents,
            style
        }
    }

    const printSuspiciousComponents = (vm: PossibleViewModes): HandlerResult => {
        const baseLineComponentsContent = () => {
            const baseLineViolations = dal._getBaseLineViolations()
            if (!baseLineViolations[vm]) return EMPTY_STRING
            return Object.keys(baseLineViolations[vm]).reduce((warning, id) => {
                const componentsDetails = {
                    id,
                    ...getComponentPageData({id, type: vm}),
                    warning: EMPTY_STRING
                }
                Object.entries(baseLineViolations[vm][id]).forEach(([type, violation]) => {
                    componentsDetails.warning += `${type} - ${violation.message}`
                })
                warning += `${JSON.stringify(componentsDetails, null, 2)}\n`
                return warning
            }, EMPTY_STRING)
        }

        const toManyChildrenWithTheSameType = () => {
            const viewModeComponents = getAllComponents(vm)
            let message = EMPTY_STRING
            Object.values(viewModeComponents).forEach(comp => {
                const childrenTypesOccurrences: Record<string, number> = comp.components?.reduce(
                    (childrenTypesInComp: Record<string, number>, childId: string) => {
                        const childData = dal.get({id: childId, type: vm})
                        const {componentType} = childData
                        if (!childrenTypesInComp[componentType]) childrenTypesInComp[componentType] = 0
                        childrenTypesInComp[componentType]++
                        return childrenTypesInComp
                    },
                    {}
                )
                if (!childrenTypesOccurrences) return
                for (const [key, value] of Object.entries(childrenTypesOccurrences)) {
                    if (value > NUMBER_OF_CHILD_WITH_SAME_TYPE) {
                        const warningsData = {
                            id: comp.id,
                            ...getComponentPageData({
                                id: comp.id,
                                type: vm
                            }),
                            warning: `has ${value} children from type ${key}`
                        }
                        message += `${JSON.stringify(warningsData, null, 2)}\n`
                    }
                }
            })
            return message
        }
        const content = `${baseLineComponentsContent()}${toManyChildrenWithTheSameType()}`
        return {
            title: `${SUSPICIOUS_COMPONENTS_TITLE} ${vm}`,
            content,
            style: INFO_STYLE
        }
    }

    return {
        generalSiteHandlers: {
            printNumberOfPages,
            printDiffBetweenAppsStateAndClientSpecMap
        },
        viewModeSiteHandlers: {
            printSuspiciousPages,
            printNumberOfComponents,
            printSuspiciousComponents
        }
    }
}
